import { PublicLayout } from '../../components/marketing/PublicLayout'
import { Link } from 'react-router-dom'

export default function PrivacyPolicyPage() {
  return (
    <PublicLayout>
      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-deep-charcoal mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600">
              Effective Date: January 20, 2025 • Last Updated: January 20, 2025
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            {/* Introduction */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Introduction</h2>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>Welcome to TalkToast!</p>
                <p>
                  TalkToast LLC ("TalkToast", "we", "us", or "our") operates the TalkToast web application and related services (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
                </p>
                <p>
                  Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.
                </p>
              </div>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Information We Collect</h2>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3 mt-6">Information You Provide to Us</h3>
              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="text-xl font-semibold text-deep-charcoal mb-2">Account Information:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li>Email address (for authentication)</li>
                    <li>Display name (optional)</li>
                    <li>Password (encrypted and hashed)</li>
                    <li>Authentication provider data (if using Google, Apple, or Facebook login)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-deep-charcoal mb-2">Dietary Preferences and Settings:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li>Dietary restrictions (allergies, preferences)</li>
                    <li>Primary dietary preference (omnivore, vegan, keto, etc.)</li>
                    <li>Cuisine preferences and strategy</li>
                    <li>Kitchen appliance availability</li>
                    <li>Household size and cooking preferences</li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-deep-charcoal mb-2">User-Generated Content:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li>Meal plan configurations and selections</li>
                    <li>Custom recipes you upload</li>
                    <li>Recipe ratings and reviews</li>
                    <li>Shopping lists and calendar entries</li>
                    <li>Feedback and bug reports</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3 mt-6">Calendar Integration</h3>
              <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan space-y-3">
                <p className="text-base text-gray-700">
                  <strong>Calendar Permissions:</strong> TalkToast may request permission to add events to your device calendar to sync meal plans.
                </p>
                <div className="space-y-2 text-base text-gray-600">
                  <p><strong>What we write to your calendar:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Meal plan events with recipe names, dates, and times</li>
                    <li>.ics calendar files you can download and import</li>
                    <li>Cooking reminders and meal prep notifications</li>
                  </ul>
                </div>
                <p className="text-base text-gray-700 font-semibold">
                  <strong>Important:</strong> We do NOT read your existing calendar events or access other calendar data. We only write meal plan events when you choose to export them.
                </p>
                <p className="text-sm text-gray-500">
                  Calendar permissions are optional. You can use TalkToast without granting calendar access by manually downloading .ics files.
                </p>
              </div>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3 mt-6">Information Collected Automatically</h3>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>When you use our Service, we automatically collect certain information about your device and usage:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Pages visited and features used</li>
                  <li>Browser type and device information</li>
                  <li>IP address</li>
                  <li>Usage analytics (first-party only)</li>
                </ul>
                <p><strong>We do NOT use:</strong> Third-party advertising cookies, cross-site tracking, or behavioral advertising networks.</p>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">How We Use Your Information</h2>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>We use your information to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide and maintain the Service (meal planning, recipe generation, shopping lists)</li>
                  <li>Generate personalized meal plans using AI</li>
                  <li>Store your recipes, favorites, and meal history</li>
                  <li>Enable integrations with grocery partners (Instacart, etc.)</li>
                  <li>Improve and optimize the Service</li>
                  <li>Communicate with you about service updates</li>
                  <li>Respond to your support requests</li>
                  <li>Comply with legal obligations and protect against fraud</li>
                </ul>
              </div>
            </section>

            {/* How We Share Your Information */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">How We Share Your Information</h2>
              <div className="bg-toast-gold/10 border border-toast-gold/30 rounded-lg p-6 mb-6">
                <p className="text-lg font-semibold text-deep-charcoal mb-2">
                  We do NOT sell your personal information to third parties.
                </p>
              </div>

              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>We share your information only with trusted third-party service providers who help us operate the Service, including:</p>

                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Database and authentication services</strong> to store user profiles, meal plans, and recipes securely</li>
                  <li><strong>AI service providers</strong> for recipe generation (does NOT receive your email or personal identifiers)</li>
                  <li><strong>Web hosting and analytics platforms</strong> to host our application and provide first-party analytics</li>
                  <li><strong>Grocery delivery partners</strong> that receive shopping list data only when you choose to export</li>
                </ul>

                <p className="mt-4">
                  All service providers are contractually obligated to protect your data and use it only for providing services to TalkToast.
                </p>
              </div>
            </section>

            {/* Your Privacy Rights */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Your Privacy Rights</h2>

              <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan mb-6">
                <h3 className="text-xl font-semibold text-deep-charcoal mb-4">All Users</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Access Your Data:</strong> View and download your data via Settings → Privacy & Data</li>
                  <li><strong>Update Your Information:</strong> Edit your profile and preferences in Settings</li>
                  <li><strong>Delete Your Account:</strong> Permanently delete your account and all data</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails (you'll still receive essential service emails)</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-deep-charcoal mb-3">California Residents (CCPA/CPRA)</h3>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>California residents have additional rights under the California Consumer Privacy Act:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Right to know what personal information we collect and how we use it</li>
                  <li>Right to delete your personal information</li>
                  <li>Right to opt-out of sale (we do NOT sell your data)</li>
                  <li>Right to non-discrimination for exercising your rights</li>
                </ul>
                <p>
                  Contact <a href="mailto:privacy@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">privacy@talktoast.com</a> to exercise your rights. We will respond within 45 days.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-deep-charcoal mb-3 mt-6">Washington State Residents</h3>
              <p className="text-base text-gray-600">
                As a Washington-based company, you have rights to access, correct, delete, and port your data. Contact <a href="mailto:privacy@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">privacy@talktoast.com</a> to exercise these rights.
              </p>
            </section>

            {/* Global Privacy Control */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Global Privacy Control (GPC)</h2>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>TalkToast respects and honors the Global Privacy Control (GPC) browser signal.</p>
                <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan">
                  <h3 className="text-lg font-semibold text-deep-charcoal mb-2">How We Honor GPC:</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                    <li>Vercel Analytics automatically disables tracking for GPC-enabled users</li>
                    <li>We do not sell or share personal information (GPC or not)</li>
                    <li>GPC users receive the same service as all users</li>
                  </ul>
                  <p className="mt-4">
                    <strong>Enable GPC:</strong> Available in Firefox Settings, Brave (enabled by default), or via Privacy Badger extension for Chrome/Edge.
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Learn more: <a href="https://globalprivacycontrol.org" target="_blank" rel="noopener noreferrer" className="text-toast-gold-700 hover:text-toast-gold-800">globalprivacycontrol.org</a>
                  </p>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Data Security</h2>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>We implement appropriate security measures to protect your information:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Encryption in transit (HTTPS/TLS)</li>
                  <li>Encryption at rest (database encryption)</li>
                  <li>Secure password hashing (bcrypt)</li>
                  <li>Regular security audits</li>
                  <li>Access controls and authentication</li>
                </ul>
                <p>
                  <strong>Important:</strong> No method of transmission or storage is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
                </p>
              </div>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Children's Privacy</h2>
              <p className="text-base text-gray-600 leading-relaxed">
                TalkToast is intended for users aged 18 and older. We do not knowingly collect personal information from anyone under 18. If you are under 18, please do not use the Service. If we learn we have collected information from a user under 18, we will delete it immediately. Contact <a href="mailto:privacy@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">privacy@talktoast.com</a> if you believe we might have information from a minor.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Changes to This Privacy Policy</h2>
              <div className="space-y-4 text-base text-gray-600 leading-relaxed">
                <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Email notification to your registered email address</li>
                  <li>In-app notification upon next login</li>
                  <li>Updated "Last Updated" date at the top of this policy</li>
                </ul>
                <p>
                  For material changes, we will provide at least 30 days' notice before the changes take effect. Continued use after the effective date constitutes acceptance of the updated policy.
                </p>
              </div>
            </section>

            {/* Contact Us */}
            <section className="bg-gradient-to-br from-toast-gold/10 to-transparent rounded-lg p-6 border border-toast-gold/20">
              <h2 className="text-2xl font-bold text-deep-charcoal mb-4">Questions About Your Privacy?</h2>
              <p className="text-base text-gray-600 mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-base text-gray-700">
                  <strong>Privacy Requests:</strong> <a href="mailto:privacy@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800 font-semibold">privacy@talktoast.com</a>
                </p>
                <p className="text-base text-gray-700">
                  <strong>General Support:</strong> <a href="mailto:support@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">support@talktoast.com</a>
                </p>
                <p className="text-base text-gray-700">
                  <strong>Legal Inquiries:</strong> <a href="mailto:legal@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">legal@talktoast.com</a>
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                We will respond to privacy requests within 30 days (45 days for California residents).
              </p>
            </section>

            {/* Footer */}
            <div className="text-center pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">
                <strong>Last Updated:</strong> January 20, 2025 • <strong>Effective Date:</strong> January 20, 2025
              </p>
              <p className="text-sm text-gray-500">
                TalkToast LLC • Tacoma, Washington, United States
              </p>
              <div className="mt-6">
                <Link
                  to="/terms-of-service"
                  className="text-toast-gold-700 hover:text-toast-gold-800 font-medium"
                >
                  View Terms of Service →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
